// Thin shim over styles.module.css, mirroring components/shared/styledComponents.
// The `$`-prefixed variant props must stay destructured off here — they are not
// valid DOM attributes and React warns if they reach the element.
import React, { forwardRef } from 'react';
import styles from './styles.module.css';

const cx = (...classes: (string | undefined | false)[]) =>
  classes.filter(Boolean).join(' ');

type DivProps = React.ComponentPropsWithoutRef<'div'>;
type ButtonProps = React.ComponentPropsWithoutRef<'button'>;

export const Void = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.void, className)} {...rest} />
  ),
);
Void.displayName = 'Void';

export const Stage = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.stage, className)} {...rest} />
  ),
);
Stage.displayName = 'Stage';

export const CanvasHost = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.canvasHost, className)} {...rest} />
  ),
);
CanvasHost.displayName = 'CanvasHost';

export const Wordmark = forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<'a'>
>(({ className, ...rest }, ref) => (
  <a ref={ref} className={cx(styles.wordmark, className)} {...rest} />
));
Wordmark.displayName = 'Wordmark';

export const WordmarkLogo = forwardRef<
  HTMLImageElement,
  Omit<React.ComponentPropsWithoutRef<'img'>, 'alt'> & { alt: string }
>(({ className, alt, ...rest }, ref) => (
  <img
    ref={ref}
    alt={alt}
    className={cx(styles.wordmarkLogo, className)}
    {...rest}
  />
));
WordmarkLogo.displayName = 'WordmarkLogo';

export const WordmarkName = forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<'span'>
>(({ className, ...rest }, ref) => (
  <span ref={ref} className={cx(styles.wordmarkName, className)} {...rest} />
));
WordmarkName.displayName = 'WordmarkName';

export const ShareCaption = forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<'p'>
>(({ className, ...rest }, ref) => (
  <p ref={ref} className={cx(styles.shareCaption, className)} {...rest} />
));
ShareCaption.displayName = 'ShareCaption';

export const OrbWrap = forwardRef<
  HTMLDivElement,
  DivProps & {
    /** accepted for call-site parity only — the reveal is driven by the canvas layers */
    $revealed: boolean;
    $perched: boolean;
    $pressed: boolean;
  }
>(({ className, $revealed, $perched, $pressed, ...rest }, ref) => (
  <div
    ref={ref}
    className={cx(
      styles.orbWrap,
      $pressed && styles.orbWrapPressed,
      $perched && styles.orbWrapPerched,
      className,
    )}
    {...rest}
  />
));
OrbWrap.displayName = 'OrbWrap';

export const OrbButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, ...rest }, ref) => (
    <button ref={ref} className={cx(styles.orbButton, className)} {...rest} />
  ),
);
OrbButton.displayName = 'OrbButton';

export const HoldHint = forwardRef<
  HTMLDivElement,
  DivProps & { $visible: boolean }
>(({ className, $visible, ...rest }, ref) => (
  <div
    ref={ref}
    className={cx(
      styles.holdHint,
      $visible && styles.holdHintVisible,
      className,
    )}
    {...rest}
  />
));
HoldHint.displayName = 'HoldHint';

export const Scene = forwardRef<
  HTMLElement,
  React.ComponentPropsWithoutRef<'section'> & { $on: boolean }
>(({ className, $on, ...rest }, ref) => (
  <section
    ref={ref}
    className={cx(styles.scene, $on && styles.sceneOn, className)}
    {...rest}
  />
));
Scene.displayName = 'Scene';

export const Heading = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.heading, className)} {...rest} />
  ),
);
Heading.displayName = 'Heading';

export const SubText = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.subText, className)} {...rest} />
  ),
);
SubText.displayName = 'SubText';

export const Field = forwardRef<
  HTMLInputElement,
  React.ComponentPropsWithoutRef<'input'> & { $shake?: boolean }
>(({ className, $shake, ...rest }, ref) => (
  <input
    ref={ref}
    className={cx(styles.field, $shake && styles.fieldShake, className)}
    {...rest}
  />
));
Field.displayName = 'Field';

export const FieldFrame = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.fieldFrame, className)} {...rest} />
  ),
);
FieldFrame.displayName = 'FieldFrame';

export const FauxCaret = forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<'span'>
>(({ className, ...rest }, ref) => (
  <span ref={ref} className={cx(styles.fauxCaret, className)} {...rest} />
));
FauxCaret.displayName = 'FauxCaret';

export const CityGroup = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.cityGroup, className)} {...rest} />
  ),
);
CityGroup.displayName = 'CityGroup';

export const Ghost = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, ...rest }, ref) => (
    <button
      ref={ref}
      data-haptic-tap="true"
      className={cx(styles.ghost, className)}
      {...rest}
    />
  ),
);
Ghost.displayName = 'Ghost';

export const SceneTop = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.sceneTop, className)} {...rest} />
  ),
);
SceneTop.displayName = 'SceneTop';

export const SceneBottom = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.sceneBottom, className)} {...rest} />
  ),
);
SceneBottom.displayName = 'SceneBottom';

export const SceneSpacer = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.sceneSpacer, className)} {...rest} />
  ),
);
SceneSpacer.displayName = 'SceneSpacer';

export const NameQuestion = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.nameQuestion, className)} {...rest} />
  ),
);
NameQuestion.displayName = 'NameQuestion';

/** `$block` defaults to the full-width branch, as main's `$block = true` did. */
export const PrimaryCta = forwardRef<
  HTMLButtonElement,
  ButtonProps & { $block?: boolean }
>(({ className, $block = true, ...rest }, ref) => (
  <button
    ref={ref}
    data-haptic-tap="true"
    className={cx(styles.cta, !$block && styles.ctaAuto, className)}
    {...rest}
  />
));
PrimaryCta.displayName = 'PrimaryCta';

export const OutlineCta = forwardRef<
  HTMLButtonElement,
  ButtonProps & { $block?: boolean }
>(({ className, $block = true, ...rest }, ref) => (
  <button
    ref={ref}
    data-haptic-tap="true"
    className={cx(
      styles.cta,
      styles.outlineCta,
      !$block && styles.ctaAuto,
      className,
    )}
    {...rest}
  />
));
OutlineCta.displayName = 'OutlineCta';

export const Note = forwardRef<HTMLDivElement, DivProps & { $busy?: boolean }>(
  ({ className, $busy, ...rest }, ref) => (
    <div
      ref={ref}
      className={cx(styles.note, $busy && styles.noteBusy, className)}
      {...rest}
    />
  ),
);
Note.displayName = 'Note';

export const TextScrim = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.textScrim, className)} {...rest} />
  ),
);
TextScrim.displayName = 'TextScrim';

export const InsightBlock = forwardRef<
  HTMLDivElement,
  DivProps & { $visible: boolean }
>(({ className, $visible, ...rest }, ref) => (
  <div
    ref={ref}
    className={cx(
      styles.insightBlock,
      $visible && styles.insightBlockVisible,
      className,
    )}
    {...rest}
  />
));
InsightBlock.displayName = 'InsightBlock';

export const InsightTitle = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.insightTitle, className)} {...rest} />
  ),
);
InsightTitle.displayName = 'InsightTitle';

export const InsightBody = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.insightBody, className)} {...rest} />
  ),
);
InsightBody.displayName = 'InsightBody';

export const DragHint = forwardRef<HTMLDivElement, DivProps & { $on: boolean }>(
  ({ className, $on, ...rest }, ref) => (
    <div
      ref={ref}
      className={cx(styles.dragHint, $on && styles.dragHintOn, className)}
      {...rest}
    />
  ),
);
DragHint.displayName = 'DragHint';

export const CarouselHost = forwardRef<
  HTMLDivElement,
  DivProps & { $visible: boolean }
>(({ className, $visible, ...rest }, ref) => (
  <div
    ref={ref}
    className={cx(
      styles.carouselHost,
      $visible && styles.carouselHostVisible,
      className,
    )}
    {...rest}
  />
));
CarouselHost.displayName = 'CarouselHost';

export const TagCard = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.tagCard, className)} {...rest} />
  ),
);
TagCard.displayName = 'TagCard';

export const DragSurface = forwardRef<
  HTMLDivElement,
  DivProps & { $active: boolean; $grabbing: boolean }
>(({ className, $active, $grabbing, ...rest }, ref) => (
  <div
    ref={ref}
    className={cx(
      styles.dragSurface,
      $active && styles.dragSurfaceActive,
      $grabbing && styles.dragSurfaceGrabbing,
      className,
    )}
    {...rest}
  />
));
DragSurface.displayName = 'DragSurface';
